import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Workflow, Pause, Play, Square, CalendarClock, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  running:   { label: "Running",   icon: Play,          color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  paused:    { label: "Paused",    icon: Pause,         color: "text-amber-600",   bg: "bg-amber-50 border-amber-200" },
  completed: { label: "Completed", icon: CheckCircle2,  color: "text-slate-500",   bg: "bg-slate-50 border-slate-200" },
  stopped:   { label: "Stopped",   icon: XCircle,       color: "text-slate-500",   bg: "bg-slate-50 border-slate-200" },
};

function fmtNextRun(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = d - Date.now();
  if (diffMs < -60_000) return "ready now";
  if (diffMs < 60_000) return "in <1 min";
  if (diffMs < 3600_000) return `in ${Math.round(diffMs / 60000)} min`;
  if (diffMs < 86_400_000) return `in ${Math.round(diffMs / 3_600_000)} hr`;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // yyyy-MM-ddThh:mm
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ProjectAutomations({ projectId }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reschedTarget, setReschedTarget] = useState(null);
  const [reschedDate, setReschedDate] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/workflow-executions?project_id=${projectId}`);
      setExecutions(res.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [projectId, load]);

  const act = async (id, verb) => {
    try {
      await axios.post(`${API_URL}/workflow-executions/${id}/${verb}`);
      toast.success(`Automation ${verb}${verb.endsWith("e") ? "d" : "ed"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb}`);
    }
  };

  const saveReschedule = async () => {
    if (!reschedTarget || !reschedDate) return;
    try {
      const iso = new Date(reschedDate).toISOString();
      await axios.post(`${API_URL}/workflow-executions/${reschedTarget.id}/reschedule`, { next_run_at: iso });
      toast.success("Next step rescheduled");
      setReschedTarget(null);
      setReschedDate("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reschedule");
    }
  };

  const active = executions.filter(e => e.status === "running" || e.status === "paused");
  const past = executions.filter(e => e.status === "completed" || e.status === "stopped").slice(0, 3);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-xs text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading automations…
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="project-automations-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Workflow className="w-4 h-4 text-[#0F4C81]" />
            Automations
            {active.length > 0 && (
              <span className="text-xs font-normal text-slate-500">
                ({active.length} active)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {active.length === 0 && past.length === 0 && (
            <p className="text-xs text-slate-400 py-4 text-center">No automations for this project yet.</p>
          )}

          {active.map(e => {
            const meta = STATUS_META[e.status] || STATUS_META.running;
            const Icon = meta.icon;
            return (
              <div
                key={e.id}
                className={`rounded-lg border p-3 ${meta.bg}`}
                data-testid={`automation-${e.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                      <p className="text-sm font-medium text-slate-800 truncate">{e.workflow_name || "Automation"}</p>
                      <span className={`text-[10px] uppercase tracking-wide font-semibold ${meta.color}`}>{meta.label}</span>
                    </div>
                    {e.next_step_label && (
                      <p className="text-xs text-slate-600 mt-1">
                        Next: <b>{e.next_step_label}</b>
                        <span className="text-slate-400 ml-2">
                          <Clock className="w-3 h-3 inline mr-0.5" />
                          {fmtNextRun(e.next_run_at)}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {e.status === "running" && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => act(e.id, "pause")} data-testid={`pause-${e.id}`}>
                        <Pause className="w-3 h-3 mr-1" /> Pause
                      </Button>
                    )}
                    {e.status === "paused" && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => act(e.id, "resume")} data-testid={`resume-${e.id}`}>
                        <Play className="w-3 h-3 mr-1" /> Resume
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      onClick={() => { setReschedTarget(e); setReschedDate(toDatetimeLocal(e.next_run_at || new Date().toISOString())); }}
                      data-testid={`reschedule-${e.id}`}
                    >
                      <CalendarClock className="w-3 h-3 mr-1" /> Reschedule
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-600 border-red-100"
                      onClick={() => act(e.id, "stop")}
                      data-testid={`stop-${e.id}`}
                    >
                      <Square className="w-3 h-3 mr-1" /> Stop
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          {past.length > 0 && (
            <div className="pt-2 border-t border-slate-100 space-y-1">
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Recent</p>
              {past.map(e => {
                const meta = STATUS_META[e.status] || STATUS_META.completed;
                return (
                  <div key={e.id} className="flex items-center gap-2 text-xs text-slate-500 py-1">
                    <span className={meta.color}>{meta.label}</span>
                    <span className="truncate">{e.workflow_name || "Automation"}</span>
                    {e.stop_reason && (
                      <span className="text-[10px] text-slate-400 truncate">— {e.stop_reason}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reschedule modal */}
      <Dialog open={!!reschedTarget} onOpenChange={(o) => !o && setReschedTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Reschedule Next Step</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {reschedTarget && (
              <p className="text-xs text-slate-500">
                <b>{reschedTarget.workflow_name}</b><br />
                Next: {reschedTarget.next_step_label || "—"}
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs">New send time</Label>
              <Input
                type="datetime-local"
                value={reschedDate}
                onChange={(e) => setReschedDate(e.target.value)}
                className="h-10"
                data-testid="reschedule-datetime"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 h-10" onClick={() => setReschedTarget(null)}>Cancel</Button>
              <Button className="flex-1 h-10 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={saveReschedule} data-testid="reschedule-save-btn">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
